/**
 * ATG — tank gauges, station groups, tank array data and the add/edit/delete
 * tank modals, merged into one module[cite: 1].
 *
 * Replaces four files that used to live in two folders:
 *   ArrayCreators/TankArrayCreator.js        -> the page + data helpers
 *   components/TankComponents/TankGauge.js   -> the gauge card
 *   components/TankComponents/TankModals.js  -> add / edit / delete modals
 *   (plus their three stylesheets, now ATG.css)[cite: 1]
 *
 * They were merged rather than split two-and-two because TankModals imported
 * TankArrayData/ProductData from TankArrayCreator while TankArrayCreator
 * imported TankGauge — keeping them in separate modules under src/Reports
 * would have created a circular import, which can leave a component
 * undefined at module-evaluation time.[cite: 1]
 *
 * All logic is unchanged; only the import paths and file layout differ[cite: 1].
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Autocomplete } from '@material-ui/lab';
import TextField from '@material-ui/core/TextField';
import { Modal } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Badge from 'react-bootstrap/Badge';
import { color } from 'd3-color';
import LiquidFillGauge from 'react-liquid-gauge';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import 'sweetalert2/src/sweetalert2.scss';
import './ATG.css';
import { API_BASE } from '../config';
import { tr } from '../i18n';

/* ======================================================================
   AUTOMATION TUNNEL & SSO (COMMENTED OUT)
   ====================================================================== */
// const STATION_USER = 'lakeoil';
// const STATION_PASS = 'lakeoil12';
// 
// const STATION_TANK_REPORTS_ROUTE = '/TankReports';
// 
// const tankReportsHref = (stationUrl) => {
//   if (!stationUrl) return null;
//   const [base] = stationUrl.split('#');
//   try {
//     const payload = btoa(unescape(encodeURIComponent(`${STATION_USER}:${STATION_PASS}`)));
//     return `${base}?sso=${encodeURIComponent(payload)}#${STATION_TANK_REPORTS_ROUTE}`;
//   } catch (e) {
//     return `${base}#${STATION_TANK_REPORTS_ROUTE}`;
//   }
// };

/* ======================================================================
   Tank gauge card
   ====================================================================== */

export function TankGauge({ value, name, capacity, temperature, value15, water, product, updated }) {
    const [showDetails, setShowDetails] = useState(false);
    
    const radius = 60; // Smaller radius for compact layout

    const ratio = capacity > 0 ? Math.min(Math.max(value / capacity, 0), 1) : 0;
    const percent = ratio * 100;

    const fillColor =
        percent < 30 ? '#e2211c' :
        percent < 70 ? '#ffc100' : '#00aa68';
    const gradientStops = [
        {
            key: '0%',
            stopColor: color(fillColor).darker(0.5).toString(),
            stopOpacity: 1,
            offset: '0%'
        },
        {
            key: '50%',
            stopColor: fillColor,
            stopOpacity: 0.75,
            offset: '50%'
        },
        {
            key: '100%',
            stopColor: color(fillColor).brighter(0.5).toString(),
            stopOpacity: 0.5,
            offset: '100%'
        }
    ];

    const toggleDetails = () => {
        setShowDetails(!showDetails);
    };

    const isUnleaded = product && product.toLowerCase().includes('unleaded');

    return (
        <div id="LiquidGauge" className="compact">
            <div className="gauge-header">
                <Badge className="name-badge" id="NameBadge">{name}</Badge>
                <div className="toggle-details" onClick={toggleDetails}>
                    <i className={`fas fa-chevron-${showDetails ? 'up' : 'down'}`}></i>
                </div>
            </div>
            
            <div className="gauge-content">
                <div className="gauge-container">
                    <LiquidFillGauge
                        style={{ margin: '0 auto' }}
                        width={radius * 2}
                        height={radius * 2}
                        value={percent}
                        percent="%"
                        textSize={1}
                        textOffsetX={0}
                        textOffsetY={0}
                        textRenderer={(props) => {
                            const value = Math.round(props.value);
                            const radius = Math.min(props.height / 2, props.width / 2);
                            const textPixels = (props.textSize * radius / 2);
                            const valueStyle = {
                                fontSize: textPixels
                            };
                            const percentStyle = {
                                fontSize: textPixels * 0.6
                            };
     
                            return (
                                <tspan>
                                    <tspan className="value" style={valueStyle}>{value}</tspan>
                                    <tspan style={percentStyle}>{props.percent}</tspan>
                                </tspan>
                            );
                        }}
                        riseAnimation
                        waveAnimation
                        waveFrequency={2}
                        waveAmplitude={1}
                        gradient
                        gradientStops={gradientStops}
                        circleStyle={{
                            fill: fillColor
                        }}
                        waveStyle={{
                            fill: fillColor
                        }}
                        textStyle={{
                            fill: '#f1f5f9',
                            fontFamily: 'Inter, Arial, sans-serif',
                            fontWeight: 700
                        }}
                        waveTextStyle={{
                            fill: '#0b0b0d',
                            fontFamily: 'Inter, Arial, sans-serif',
                            fontWeight: 700
                        }}
                    />
                    <div className="gauge-summary">
                        <Badge 
                            className={`product-badge ${isUnleaded ? 'unleaded' : 'diesel'}`} 
                            id="ProductBadge"
                        >
                            {product}
                        </Badge>
                        <div className="volume-info">
                            <span>{tr("{0} / {1} L", Number(value || 0).toLocaleString(), Number(capacity || 0).toLocaleString())}</span>
                        </div>
                        <div className="updated-info">
                            <span>{updated ? new Date(updated).toLocaleString() : '—'}</span>
                        </div>
                    </div>
                </div>
                
                {showDetails && (
                    <div className="details-section">
                        <div className="details-grid">
                            <Badge className="details-badge">{tr("Capacity: {0} L", capacity.toLocaleString())}</Badge>
                            <Badge className="details-badge">{tr("Current: {0} L", value.toLocaleString())}</Badge>
                            <Badge className="details-badge">{tr("@15°C: {0} L", value15.toLocaleString())}</Badge>
                            <Badge className="details-badge">{tr("Water: {0} L", water.toLocaleString())}</Badge>
                            <Badge className="details-badge">{tr("Temp: {0}°C", temperature)}</Badge>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

/* ======================================================================
   Add / Edit / Delete tank modals
   ====================================================================== */

const StationTankGroup = ({ stationInfo, tanks, token, stationsList }) => {
  const [stationName, setStationName] = useState('');
  const [stationUrl, setStationUrl] = useState('');

  useEffect(() => {
    if (stationsList && stationsList.length > 0) {
      const matchingStation = stationsList.find(station => {
        const licenseMatch = station.EWURALicenseNo === stationInfo.EWURALicenseNo;
        const serialMatch = station.LicenseeTraSerialNo === stationInfo.LicenseeTraSerialNo;
        return licenseMatch && serialMatch;
      });

      setStationName(matchingStation ? matchingStation.RetailStationName : '');
      setStationUrl(matchingStation ? (matchingStation.station_url || '') : '');
    }
  }, [stationsList, stationInfo]);

  const isOnline = tanks.length > 0;
  
  // Automation tunnel dependent variable disabled
  const reportsHref = null; // tankReportsHref(stationUrl);

  return (
    <div className="station-group">
      <div className="station-header">
        <div className="station-heading">
          <h4>{stationName || tr("Station Information")}</h4>
          <div className="station-info">
            <span><strong>{tr("License No:")}</strong> {stationInfo.EWURALicenseNo}</span>
            <span><strong>{tr("Serial No:")}</strong> {stationInfo.LicenseeTraSerialNo}</span>
          </div>
        </div>
        <div className="station-meta">
          {isOnline && (
            <span className="station-tank-count">
              {tanks.length !== 1 ? tr("{0} tanks", tanks.length) : tr("{0} tank", tanks.length)}
            </span>
          )}
          {/* Automation tunnel link dependent element commented out */}
          {/* reportsHref && (
            <a
              className="station-reports-link"
              href={reportsHref}
              target="_blank"
              rel="noopener noreferrer"
              title="Open this station's Tank Reports"
            >
              <i className="fas fa-external-link-alt" />
              Tank Reports
            </a>
          ) */}
        </div>
      </div>
      <div className="tank-container">
        {tanks.length === 0 ? (
          <div className="no-tanks-notice">
            <i className="fas fa-exclamation-triangle"></i>
            {tr("No tank data received for this station.")}
          </div>
        ) : (
          tanks.map((tank, index) => (
            <TankGauge
              key={index}
              ProbeID={tank.probe_id}
              name={tank.tank_name}
              id={tank.tank_id}
              product={tank.product_name}
              value={tank.fuel_volume}
              value15={tank.fuel_volume_15}
              water={tank.water_volume}
              capacity={tank.tank_capacity}
              updated={tank.updated_at}
              temperature={tank.average_temp}
              LicenseeTraSerialNo={tank.LicenseeTraSerialNo}
            />
          ))
        )}
      </div>
    </div>
  );
};

export const TankArrayCreator = ({ token, tankArrayCheckChange }) => {
  const [tankArray, setTankArray] = useState([]);
  const [groupedTanks, setGroupedTanks] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [stationsList, setStationsList] = useState([]);
  
  const [selectedStationSerial, setSelectedStationSerial] = useState('');

  const stationsListRef = useRef([]);
  useEffect(() => { stationsListRef.current = stationsList; }, [stationsList]);

  const fetchStationsList = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/stationinfo/all`, {
        method: 'GET',
        headers: {
          'Authorization': JSON.parse(sessionStorage.getItem('token')).data.access_token
        }
      });

      if (response.status === 200) {
        const stations = await response.json();
        setStationsList(stations.data || stations);
      }
    } catch (error) {
      console.error("Error fetching stations list:", error);
    }
  }, []);

  const fetchTankData = useCallback(() => {
    const abortController = new AbortController();
    setIsRefreshing(true);

    fetch(`${API_BASE}/ak4tek/tanks/all`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': JSON.parse(sessionStorage.getItem('token')).data.access_token
      },
      signal: abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Response not OK.", response);
        }
        return response.json();
      })
      .then((data) => {
        setTankArray(data.data);
        setLastUpdated(new Date());
        const groupedData = groupTanksByStation(data.data, stationsListRef.current);
        setGroupedTanks(groupedData);
        setIsRefreshing(false);
      })
      .catch((e) => {
        if (e.name === "AbortError" || abortController.signal.aborted) {
          return;
        }
        console.error("Error fetching tank data:", e);
        setIsRefreshing(false);

        if (tankArray.length === 0) {
          Swal.fire({
            title: tr("Error!"),
            text: tr("Connection problem."),
            icon: "error",
            confirmButtonText: tr("Close"),
          });
        }
      });

    return abortController;
  }, [tankArray.length]);

  useEffect(() => {
    fetchStationsList();
    const controller = fetchTankData();
    const intervalId = setInterval(() => {
      fetchTankData();
    }, 600000);
    
    return () => { 
      clearInterval(intervalId);
      controller.abort(); 
    };
  }, [fetchTankData, fetchStationsList, tankArrayCheckChange]);

  const groupTanksByStation = (tanksArray, stationsList = []) => {
    const groupedTanks = {};

    tanksArray.forEach(tank => {
      const stationKey = `${tank.EWURALicenseNo}_${tank.LicenseeTraSerialNo}`;
      if (!groupedTanks[stationKey]) {
        groupedTanks[stationKey] = {
          stationInfo: {
            EWURALicenseNo: tank.EWURALicenseNo,
            LicenseeTraSerialNo: tank.LicenseeTraSerialNo
          },
          tanks: []
        };
      }
      groupedTanks[stationKey].tanks.push(tank);
    });

    stationsList.forEach(station => {
      const stationKey = `${station.EWURALicenseNo}_${station.LicenseeTraSerialNo}`;
      if (!groupedTanks[stationKey]) {
        groupedTanks[stationKey] = {
          stationInfo: {
            EWURALicenseNo: station.EWURALicenseNo,
            LicenseeTraSerialNo: station.LicenseeTraSerialNo
          },
          tanks: []
        };
      }
    });

    return Object.values(groupedTanks).sort((a, b) => {
      const nameA = stationsList.find(s => s.LicenseeTraSerialNo === a.stationInfo.LicenseeTraSerialNo)?.RetailStationName || '';
      const nameB = stationsList.find(s => s.LicenseeTraSerialNo === b.stationInfo.LicenseeTraSerialNo)?.RetailStationName || '';
      return nameA.localeCompare(nameB);
    });
  };

  const displayedGroups = selectedStationSerial
    ? groupedTanks.filter(g => g.stationInfo.LicenseeTraSerialNo === selectedStationSerial)
    : groupedTanks;

  const totalStationsCount = groupedTanks.length;
  const onlineStationsCount = groupedTanks.filter(g => g.tanks.length > 0).length;

  return (
    <div className="tank-array-wrapper">
      
      <div className="info-cards-container">
        <div className="update-info">
          <div
            className={`update-icon${isRefreshing ? ' refreshing' : ''}`}
            onClick={() => fetchTankData()}
            title={tr("Refresh now")}
          >
            <i className={`fas ${isRefreshing ? 'fa-sync-alt' : 'fa-clock'}`}></i>
          </div>
          <div className="update-text">
            <span className="update-label">{tr("Last Updated")}</span>
            <span className="update-time">{lastUpdated.toLocaleString()}</span>
          </div>
        </div>
        
        <div className="stats-info">
          <div className="stats-icon">
            <i className="fas fa-chart-bar"></i>
          </div>
          <div className="update-text">
            <span className="update-label">{tr("Summary")}</span>
            <span className="update-time">
              {totalStationsCount !== 1 ? tr("{0} Stations", totalStationsCount) : tr("{0} Station", totalStationsCount)} {tankArray.length !== 1 ? tr("({0} Online), {1} Tanks", onlineStationsCount, tankArray.length) : tr("({0} Online), {1} Tank", onlineStationsCount, tankArray.length)}
            </span>
          </div>
        </div>
      </div>
      
      <div className="filter-section" style={{ marginBottom: '30px' }}>
        <div className="users-field" style={{ maxWidth: '400px' }}>
          <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}></label>
          <Autocomplete
            options={stationsList}
            getOptionLabel={(s) => s ? `${s.RetailStationName} (${s.LicenseeTraSerialNo})` : ''}
            value={stationsList.find(s => s.LicenseeTraSerialNo === selectedStationSerial) || null}
            onChange={(e, newValue) => setSelectedStationSerial(newValue ? newValue.LicenseeTraSerialNo : '')}
            renderInput={(params) => (
              <TextField {...params} placeholder={tr("All Stations")} variant="outlined" size="small" />
            )}
          />
        </div>
      </div>
      
      <div className="tank-array-container">
        {displayedGroups.map((group, index) => (
          <StationTankGroup
            key={index}
            stationInfo={group.stationInfo}
            tanks={group.tanks}
            token={token}
            stationsList={stationsList}
          />
        ))}
      </div>
    </div>
  );
};